/**
 * Component for accessing product data from product pages
 * Provides utilities to get current variant, SKU, price, and other product information
 * Handles PDF preview button functionality
 */

(function () {
    'use strict';

    // ============================================================================
    // CONFIGURATION
    // ============================================================================
    const CONFIG = {
        PDF_BASE_URL: 'https://pdf-api.connect.m11r.eu',
        PDF_BUTTON_TEXT: 'Vorschau PDF',
        API_KEY: '', // Set via window.PDF_API_KEY or data attribute
        DEBOUNCE_DELAY: {
            variant: 150,
            textField: 300
        },
        SELECTORS: {
            variantId: 'input[name="id"].product-variant-id',
            optionRadio: 'input[type="radio"][name^="option-"]',
            sku: ['.current-variant--sku', '[data-variant-sku]', 'span[data-variant-sku]'],
            textFields: {
                greeting: 'textarea[name="properties[Anrede]"]',
                text: 'textarea[name="properties[Wunschtext]"]',
                from: 'textarea[name="properties[Von]"]'
            },
            pdfButton: 'a.product-block-button, a[class*="dynamic-section-button"]',
            productInformation: 'product-information'
        }
    };

    /**
     * Get the nearest product-information element for current product context.
     * Used to scope data lookups (Quick View vs. full product page).
     * @param {HTMLElement|null} [fallbackEl]
     * @returns {HTMLElement|null}
     */
    function getProductInformationRoot(fallbackEl = null) {
        if (fallbackEl?.closest) {
            const closest = fallbackEl.closest(CONFIG.SELECTORS.productInformation);
            if (closest) return closest;
        }

        const variantIdInput = document.querySelector(CONFIG.SELECTORS.variantId);
        return variantIdInput?.closest(CONFIG.SELECTORS.productInformation) || null;
    }

    /**
     * Parse JSON stored in `data-product-json` attribute.
     * @param {HTMLElement|null} root
     * @returns {Object|null}
     */
    function getProductJsonFromRoot(root) {
        const jsonStr = root?.dataset?.productJson;
        if (!jsonStr) return null;
        try {
            return JSON.parse(jsonStr);
        } catch (e) {
            console.warn('Could not parse data-product-json', e);
            return null;
        }
    }

    // ============================================================================
    // STATE MANAGEMENT
    // ============================================================================
    const State = {
        lastVariantId: null,
        logTimeout: null,
        textFieldTimeout: null,
        isLogging: false
    };

    // ============================================================================
    // PRODUCT DATA MODULE
    // ============================================================================
    const ProductData = {
        /**
         * Get productJson from DOM dataset (preferred) or global window fallback.
         * @returns {Object|null}
         */
        getProductJson(fallbackEl = null) {
            const root = getProductInformationRoot(fallbackEl);
            const fromDom = getProductJsonFromRoot(root);
            if (fromDom) return fromDom;
            return window.productJson || null;
        },

        /**
         * Get the currently selected variant from the product form
         * @returns {Object|null} The selected variant object or null if not found
         */
        getCurrentVariant(fallbackEl = null) {
            const root = getProductInformationRoot(fallbackEl);
            const variantIdInput = root?.querySelector
                ? root.querySelector(CONFIG.SELECTORS.variantId)
                : null;

            // Fallback for unexpected DOM states
            const variantIdInputFallback = document.querySelector(CONFIG.SELECTORS.variantId);
            const resolvedVariantIdInput = variantIdInput || variantIdInputFallback;

            if (!resolvedVariantIdInput) {
                return null;
            }

            const selectedVariantId = parseInt(resolvedVariantIdInput.value, 10);
            const productJson = this.getProductJson(fallbackEl);
            return productJson?.variants?.find(v => v.id === selectedVariantId) || null;
        },

        /**
         * Get SKU from DOM if not available in variant data
         * @param {Object} variant - Variant object
         * @returns {string|null} SKU value
         */
        getSkuFromDom(variant) {
            for (const selector of CONFIG.SELECTORS.sku) {
                const skuElement = document.querySelector(selector);
                if (skuElement?.textContent?.trim()) {
                    const sku = skuElement.textContent.trim();
                    // Remove label if present (e.g., "Art.-Nr.: SKU123")
                    return sku.replace(/^[^:]*:\s*/, '');
                }
            }

            // Use variant ID as fallback
            return variant?.id ? variant.id.toString() : null;
        },

        /**
         * Get product information
         * @returns {Object|null} Product data object
         */
        getProductData(fallbackEl = null) {
            const variant = this.getCurrentVariant(fallbackEl);
            const productJson = this.getProductJson(fallbackEl);
            if (!productJson) return null;

            let sku = variant?.sku || null;

            // Try to get SKU from DOM if not available
            if (!sku || sku === '') {
                sku = this.getSkuFromDom(variant);
            }

            return {
                product: productJson,
                productId: productJson.id,
                variant: variant,
                variantId: variant?.id || null,
                sku: sku,
                skuFromJson: variant?.sku || null,
                skuFromDom: sku && (!variant || !variant.sku || variant.sku === '') ? sku : null,
                price: variant?.price || null,
                title: variant?.title || null,
                available: variant?.available || false
            };
        },

        /**
         * Get text field values from form
         * @returns {Object} Object with greeting, text, and from values
         */
        getTextFieldValues() {
            const getValue = (selector) => {
                const field = document.querySelector(selector);
                return field?.value?.trim() || '';
            };

            return {
                greeting: getValue(CONFIG.SELECTORS.textFields.greeting),
                text: getValue(CONFIG.SELECTORS.textFields.text),
                from: getValue(CONFIG.SELECTORS.textFields.from)
            };
        },

        /**
         * Get gift card message if available
         * @returns {string|null} The gift card message or null
         */
        getGiftCardMessage() {
            const giftCardMessageField = document.querySelector('textarea[name="properties[Message]"]');
            return giftCardMessageField?.value || null;
        }
    };

    // ============================================================================
    // PDF HANDLER MODULE
    // ============================================================================
    const PdfHandler = {
        /**
         * Read voucher template from product-information dataset first, then fallback window.
         * @returns {string|null}
         */
        getVoucherPdfTemplate(fallbackEl = null) {
            const root = getProductInformationRoot(fallbackEl);
            const fromDom = root?.dataset?.voucherPdfTemplate;
            if (fromDom) return fromDom;
            return window.productMetafields?.custom?.voucher_pdf_template || null;
        },

        /**
         * Get API key from button data attribute, window variable, or config
         * Priority: data-pdf-api-key (button) > data-pdf-api-key (product-information) > window.PDF_API_KEY > CONFIG.API_KEY
         * @param {HTMLElement} button - Button element (optional)
         * @returns {string} API key
         */
        getApiKey(button) {
            // 1. Try to get from button data attribute (most secure - only available on button)
            if (button?.dataset?.pdfApiKey) {
                return button.dataset.pdfApiKey;
            }

            // 2. Try to get from product-information dataset (works in Quick View)
            const root = button?.closest?.(CONFIG.SELECTORS.productInformation) || getProductInformationRoot(button);
            const fromDom = root?.dataset?.pdfApiKey;
            if (fromDom) return fromDom;

            // 2. Try to get from window variable (set via Shopify settings)
            if (window.PDF_API_KEY) {
                return window.PDF_API_KEY;
            }

            // 3. Fallback to CONFIG (least secure - hardcoded)
            return CONFIG.API_KEY;
        },

        /**
         * Build URL with all parameters matching backend API format (camelCase)
         * @param {Object} productData - Product data object
         * @returns {string|null} Complete URL with all parameters
         */
        buildUrl(productData, fallbackEl = null) {
            if (!productData) {
                return null;
            }

            const url = new URL(CONFIG.PDF_BASE_URL + '/api/pdf/create');

            // Add template parameter (PDF template name)
            const voucherTemplate = this.getVoucherPdfTemplate(fallbackEl);
            if (voucherTemplate) {
                url.searchParams.set('template', voucherTemplate);
            }

            // Add amount parameter (camelCase) - use variant title which already contains "CHF 50" format
            if (productData.title) {
                url.searchParams.set('amount', productData.title);
            }

            // Add text field values
            const textFields = ProductData.getTextFieldValues();

            // Add textBody parameter (from Wunschtext field)
            if (textFields.text) {
                url.searchParams.set('textBody', textFields.text);
            }

            // Add textReceiver parameter (from Anrede field)
            if (textFields.greeting) {
                url.searchParams.set('textReceiver', textFields.greeting);
            }

            // Add textFrom parameter (from Von: field)
            if (textFields.from) {
                url.searchParams.set('textFrom', textFields.from);
            }

            return url.toString();
        },

        /**
         * Find PDF button in DOM
         * @returns {HTMLElement|null} The PDF button element or null
         */
        findButton() {
            const buttons = document.querySelectorAll(CONFIG.SELECTORS.pdfButton);
            for (const button of buttons) {
                if (button.textContent.trim().includes(CONFIG.PDF_BUTTON_TEXT)) {
                    return button;
                }
            }
            return null;
        },

        /**
         * Update button URL with current product data
         * @param {Object} productData - Product data object
         */
        updateButtonUrl(productData) {
            if (!productData) return;

            const button = this.findButton();
            if (!button) return;

            const url = this.buildUrl(productData, button);
            if (url) {
                button.setAttribute('data-pdf-url', url);
                // Don't set href - we handle clicks via JavaScript to include Authorization header
                // Keep href as javascript:void(0) to prevent direct link opening
                if (button.href && !button.href.includes('javascript:void(0)')) {
                    button.href = 'javascript:void(0)';
                }
            }

            // Set API key as data attribute if available from settings (for security)
            // This is set server-side and only available on the button element
            // Always update to ensure it's available after variant changes
            const apiKey = this.getApiKey(button);
            if (apiKey) button.setAttribute('data-pdf-api-key', apiKey);

            // Re-attach handler after URL update to ensure it works
            this.setupHandler();
        },

        /**
         * Set button loading state
         * @param {HTMLElement} button - Button element
         * @param {boolean} isLoading - Whether button is loading
         * @param {string} originalText - Original button text
         */
        setButtonState(button, isLoading, originalText) {
            if (isLoading) {
                button.dataset.originalText = originalText;
                button.textContent = 'PDF wird geladen...';
                button.style.pointerEvents = 'none';
                button.style.opacity = '0.6';
            } else {
                button.textContent = button.dataset.originalText || originalText;
                button.style.pointerEvents = '';
                button.style.opacity = '';
            }
        },

        /**
         * Handle PDF button click - fetch PDF and open in new tab
         * @param {Event} e - Click event
         */
        async handleClick(e) {
            // Prevent default link behavior immediately
            e.preventDefault();
            e.stopPropagation();

            const button = e.currentTarget;
            const buttonText = button.textContent.trim();

            if (!buttonText.includes(CONFIG.PDF_BUTTON_TEXT)) {
                return;
            }

            const productData = ProductData.getProductData(button);
            if (!productData) {
                console.error('Could not get product data');
                return;
            }

            const pdfUrl = this.buildUrl(productData, button);
            if (!pdfUrl) {
                console.error('Could not build PDF URL');
                return;
            }

            const originalText = button.textContent;
            this.setButtonState(button, true, originalText);

            try {
                // Get API key from button data attribute (most secure)
                const apiKey = this.getApiKey(button);
                if (!apiKey) {
                    throw new Error('API-Key nicht konfiguriert. Bitte in Shopify Settings setzen.');
                }


                const response = await fetch(pdfUrl, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${apiKey}`,
                        'Accept': 'application/pdf'
                    },
                    // CORS configuration - must match server settings
                    mode: 'cors',
                    credentials: 'include' // Required when server sends Access-Control-Allow-Credentials: true
                });


                // Handle different error status codes
                if (!response.ok) {
                    let errorMessage = `HTTP Fehler: ${response.status}`;

                    // Try to get error message from JSON response
                    const contentType = response.headers.get('content-type');
                    if (contentType?.includes('application/json')) {
                        try {
                            const errorData = await response.json();
                            // Handle different error response formats
                            if (errorData.error) {
                                errorMessage = errorData.error;
                            } else if (errorData.detail) {
                                errorMessage = errorData.detail;
                            } else if (errorData.title) {
                                errorMessage = errorData.title + (errorData.detail ? ': ' + errorData.detail : '');
                            } else {
                                errorMessage = JSON.stringify(errorData);
                            }
                        } catch (e) {
                            // If JSON parsing fails, use status text
                            errorMessage = response.statusText || errorMessage;
                        }
                    } else if (response.status === 401) {
                        // 401 usually has empty body
                        errorMessage = 'API-Key fehlt oder ist ungültig. Bitte prüfen Sie die Konfiguration.';
                    }

                    // Specific error messages based on status code (if not already set from JSON)
                    if (errorMessage === `HTTP Fehler: ${response.status}`) {
                        switch (response.status) {
                            case 401:
                                errorMessage = 'API-Key fehlt oder ist ungültig. Bitte prüfen Sie die Konfiguration.';
                                break;
                            case 400:
                                errorMessage = 'Ungültige Anfrage. Bitte prüfen Sie die Parameter (template, amount, textBody sind erforderlich).';
                                break;
                            case 404:
                                errorMessage = 'Template nicht gefunden. Bitte prüfen Sie den template-Parameter und URL-Kodierung bei Umlauten.';
                                break;
                            case 500:
                                errorMessage = 'Serverfehler bei der PDF-Erzeugung. Bitte versuchen Sie es später erneut.';
                                break;
                        }
                    }

                    throw new Error(errorMessage);
                }

                const blob = await response.blob();

                // Verify it's actually a PDF
                if (!blob.type.includes('pdf') && blob.size === 0) {
                    throw new Error('Ungültige PDF-Antwort vom Server.');
                }

                const blobUrl = URL.createObjectURL(blob);
                const newWindow = window.open(blobUrl, '_blank');

                if (!newWindow) {
                    throw new Error('Popup blockiert. Bitte erlauben Sie Popups für diese Seite.');
                }

                // Clean up blob URL after delay
                setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);

            } catch (error) {
                console.error('Error fetching PDF:', error);
                console.error('Error details:', {
                    name: error.name,
                    message: error.message,
                    stack: error.stack,
                    pdfUrl: pdfUrl,
                    isHttps: window.location.protocol === 'https:',
                    apiIsHttp: pdfUrl.startsWith('http://')
                });

                // Show user-friendly error message
                console.error('PDF Error:', error.message, 'URL:', pdfUrl);

                let userMessage = 'Fehler beim Laden des PDFs: ';

                // Handle specific error types
                if (error.name === 'TypeError' && (error.message.includes('fetch') || error.message.includes('Failed to fetch'))) {
                    // Network error (CORS, mixed content, or network issue)
                    const isHttps = window.location.protocol === 'https:';
                    const isHttpApi = pdfUrl.startsWith('http://');

                    if (isHttps && isHttpApi) {
                        userMessage += 'Mixed Content Error: Die Seite verwendet HTTPS, aber die API ist HTTP. ';
                        userMessage += 'Bitte HTTPS für die API aktivieren oder einen Proxy verwenden.';
                    } else {
                        userMessage += 'Netzwerkfehler. Mögliche Ursachen:\n\n';
                        userMessage += '1. CORS-Problem: API erlaubt diese Domain nicht\n';
                        userMessage += '2. API-Server nicht erreichbar\n';
                        userMessage += '3. Netzwerkverbindung unterbrochen\n\n';
                        userMessage += 'Bitte prüfen Sie:\n';
                        userMessage += '- Browser-Konsole für Details\n';
                        userMessage += '- API-URL: ' + pdfUrl + '\n';
                        userMessage += '- Netzwerk-Tab im Browser';
                    }
                } else if (error.message) {
                    userMessage += error.message;
                } else {
                    userMessage += 'Unbekannter Fehler. Bitte versuchen Sie es später erneut.';
                }
            } finally {
                this.setButtonState(button, false, originalText);
            }
        },

        /**
         * Setup PDF button click handler
         */
        setupHandler() {
            const button = this.findButton();
            if (button) {
                // Remove any existing handlers
                button.removeEventListener('click', this.handleClick);
                // Use capture phase to catch event early, before default behavior
                button.addEventListener('click', (e) => this.handleClick(e), { capture: true });
                // Also prevent default href behavior by setting href to javascript:void(0)
                if (button.href && button.href !== 'javascript:void(0)') {
                    button.dataset.originalHref = button.href;
                    button.href = 'javascript:void(0)';
                }
            }
        }
    };

    // ============================================================================
    // EVENT HANDLERS MODULE
    // ============================================================================
    const EventHandlers = {
        /**
         * Check and log variant change (only if actually changed)
         */
        checkAndLogVariantChange() {
            if (State.isLogging) return;

            const pdfButton = PdfHandler.findButton();
            const root = pdfButton?.closest(CONFIG.SELECTORS.productInformation) || null;
            const variantIdInput = root?.querySelector
                ? root.querySelector(CONFIG.SELECTORS.variantId)
                : null;

            if (!variantIdInput) return;

            const currentVariantId = parseInt(variantIdInput.value, 10);
            if (currentVariantId === State.lastVariantId || isNaN(currentVariantId)) {
                return;
            }

            State.isLogging = true;
            State.lastVariantId = currentVariantId;

            const productData = ProductData.getProductData(pdfButton || root);
            if (productData) {
                PdfHandler.updateButtonUrl(productData);
            }

            setTimeout(() => {
                State.isLogging = false;
            }, 200);
        },

        /**
         * Debounced variant change check
         */
        debouncedVariantCheck() {
            if (State.logTimeout) {
                clearTimeout(State.logTimeout);
            }
            State.logTimeout = setTimeout(
                () => this.checkAndLogVariantChange(),
                CONFIG.DEBOUNCE_DELAY.variant
            );
        },

        /**
         * Debounced text field update
         */
        debouncedTextFieldUpdate() {
            if (State.textFieldTimeout) {
                clearTimeout(State.textFieldTimeout);
            }
            State.textFieldTimeout = setTimeout(() => {
                const pdfButton = PdfHandler.findButton();
                const productData = ProductData.getProductData(pdfButton || null);
                if (productData) {
                    PdfHandler.updateButtonUrl(productData);
                }
            }, CONFIG.DEBOUNCE_DELAY.textField);
        },

        /**
         * Setup all event listeners
         */
        setup() {
            // Initialize lastVariantId (scope to current quick view product button if possible)
            const pdfButton = PdfHandler.findButton();
            const root = pdfButton?.closest(CONFIG.SELECTORS.productInformation) || null;
            const variantIdInput =
                root?.querySelector?.(CONFIG.SELECTORS.variantId) ||
                document.querySelector(CONFIG.SELECTORS.variantId);
            if (variantIdInput) State.lastVariantId = parseInt(variantIdInput.value, 10);

            // Variant change listeners
            document.addEventListener('change', (e) => {
                if (e.target.matches(CONFIG.SELECTORS.variantId) ||
                    e.target.matches(CONFIG.SELECTORS.optionRadio)) {
                    this.debouncedVariantCheck();
                }
            });

            // Text field change listeners
            const textFieldSelectors = Object.values(CONFIG.SELECTORS.textFields).join(', ');
            document.addEventListener('input', (e) => {
                if (e.target.matches(textFieldSelectors)) {
                    this.debouncedTextFieldUpdate();
                }
            });

            // EventBus listener (if available)
            if (window.eventBus?.on) {
                window.eventBus.on('variant:change', () => {
                    this.debouncedVariantCheck();
                });
                // Quick View may emit a different event name depending on the component.
                window.eventBus.on('variant:updated', () => {
                    this.debouncedVariantCheck();
                });
            }
        }
    };

    // ============================================================================
    // INITIALIZATION
    // ============================================================================
    function init() {
        // Wait for DOM to be ready
        setTimeout(() => {
            EventHandlers.setup();

            const pdfButton = PdfHandler.findButton();
            const productData = ProductData.getProductData(pdfButton || null);
            if (productData) {
                PdfHandler.updateButtonUrl(productData);
            }

            PdfHandler.setupHandler();
        }, 100);
    }

    // ============================================================================
    // PUBLIC API
    // ============================================================================
    window.ProductData = {
        getCurrentVariant: () => ProductData.getCurrentVariant(),
        getProductData: () => ProductData.getProductData(),
        getGiftCardMessage: () => ProductData.getGiftCardMessage(),
        onVariantChange: (callback) => {
            document.addEventListener('change', (e) => {
                if (e.target.matches(CONFIG.SELECTORS.variantId)) {
                    const productData = ProductData.getProductData();
                    if (callback && typeof callback === 'function') {
                        callback(productData);
                    }
                }
            });
        }
    };

    // ============================================================================
    // START
    // ============================================================================
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
